import React, { useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import { ClockIcon } from '@heroicons/react/solid';
import { isPatientFemale } from '../../../../utils/conditionChecks';
import { formatDateTime } from '../../../../utils/timestampUtils';
import { FormattedAnswer, normalizeRawAnswer, buildDisplayAnswer } from '../../../../utils/formDisplayUtils';
import { buildAnswerLookup, isSubquestionVisible, buildExternalAnswerLookups } from '../../../../utils/formVisibility';
import { refreshDobAge } from '../../../serviceUsers/3_forms/functions/PrefillFunctions';

function FormDetailsPanel({ isOpen, formInfo, patientData, informantData, onClose, displayMode = 'full' }) {
  // When the dialog is closed and formInfo is null, render only the bare
  // Dialog.Root shell so Radix Presence can still animate the exit transition.
  // Accessing formInfo properties when null would crash the component.
  const isInformantForm = formInfo?.source === 'informant';
  const formKey = formInfo?.key;

  // Derive all display data synchronously during render so the dialog opens at
  // full size immediately. A useEffect + setState here caused a visible "grow"
  // flash as content populated after the overlay had already appeared.
  const panelContent = useMemo(() => {
    if (!isOpen || !formInfo) {
      return {
        formTitle: '',
        formattedQuestions: [],
        sourceDetails: '',
        scaleResults: null,
      };
    }

    let formData;
    let dataSource;
    let sourceDetails = 'Patient';

    if (isInformantForm && formInfo.informantId && informantData[formInfo.informantId]) {
      dataSource = informantData[formInfo.informantId];
      formData = dataSource?.formData?.[formKey];

      const informantRole = patientData?.informants?.[formInfo.informantId]?.informantRole;
      const informantName = dataSource.displayName
        || [dataSource.firstName, dataSource.lastName].filter(Boolean).join(' ')
        || dataSource.email
        // CYP roles get their concrete name, never a generic "Informant".
        || (informantRole === 'legalGuardian' ? 'Parent/carer'
          : informantRole === 'schoolRep' ? 'School representative'
            : 'Informant');
      const relationship = patientData?.informants?.[formInfo.informantId]?.informantRelationshipToPatient || 'Unknown';
      sourceDetails = `${informantName} (${relationship})`;
    } else {
      dataSource = patientData;
      formData = dataSource?.formData?.[formKey];
    }

    if (!formKey || !formData) {
      return {
        formTitle: '',
        formattedQuestions: [],
        sourceDetails,
        scaleResults: null,
      };
    }

    const formTitle = formData.formTitle || `Form ${formKey}`;

    const buildPrefillLookup = (ds) => {
      const lookup = {};
      const dataModel = ds?.dataModel;
      if (!dataModel) return lookup;

      Object.values(dataModel).forEach((module) => {
        if (!module || typeof module !== 'object') return;
        Object.values(module.sections || {}).forEach((section) => {
          if (!section || typeof section !== 'object') return;
          Object.values(section.subsections || section.data || {}).forEach((subsection) => {
            if (!subsection || typeof subsection !== 'object') return;
            Object.values(subsection.subsectionQuestions || {}).forEach((question) => {
              if (!question || typeof question !== 'object') return;
              const prefill = question.patientPrefill;
              const varNames = question.prefilledByServiceUserVarNames;
              if (prefill && Array.isArray(varNames) && varNames.length === 1) {
                // The date of birth prefill carries an age fixed at the time
                // the form was submitted (or a raw ISO date for demo seeds).
                // Recalculate it for today, as the Clinical Interview does.
                lookup[varNames[0]] = question.providerVarName === 'dateOfBirth'
                  ? refreshDobAge(prefill)
                  : prefill;
              }
            });
          });
        });
      });

      return lookup;
    };

    const prefillLookup = buildPrefillLookup(dataSource);
    const formDefinition = dataSource.formData[formKey];
    const formattedQuestions = [];

    const shouldDisplaySubsection = (subsection) => {
      if (formKey === 'yourHealth' && subsection.formSubsectionTitle === "Women's Health") {
        return isPatientFemale(dataSource.formData);
      }
      return true;
    };

    const answerLookup = buildAnswerLookup(formDefinition.formSubsections || {});

    // Sibling-form lookups for `otherForm` conditions (the CYP periods
    // questions are hidden by the About form's sex assigned at birth). The
    // informant's formData map carries every form in the adult shape, so the
    // same buildAnswerLookup works for the siblings. Empty for forms with no
    // cross-form gate, so nothing else changes.
    const externalLookups = buildExternalAnswerLookups(
      formDefinition,
      dataSource.formData,
      (siblingEntry) => buildAnswerLookup(siblingEntry?.formSubsections || {}),
    );

    Object.entries(formDefinition.formSubsections || {}).forEach(([, subsection]) => {
      if (!shouldDisplaySubsection(subsection)) {
        return;
      }

      // Rows the parent never saw are left out; if that empties a whole
      // question the question heading goes too, and if it empties the whole
      // subsection (CYP "Puberty and periods" for a Male child) the
      // subsection heading goes as well, matching what the parent's form did.
      const subsectionRows = [];

      Object.entries(subsection.questions || {}).forEach(([, question]) => {
        const questionRows = [];

        Object.entries(question.subquestions || {}).forEach(([subquestionKey, subquestion]) => {
          if (isNaN(parseInt(subquestionKey, 10))) return;
          if (!isSubquestionVisible(subquestion, answerLookup, externalLookups)) return;

          const answer = normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer);
          const allOptions = [...(subquestion.options || []), ...(subquestion.noneOptions || [])];
          const prefillText = subquestion.serviceUserVarName
            ? prefillLookup[subquestion.serviceUserVarName]
            : null;

          questionRows.push({
            type: 'subquestion',
            text: subquestion.subquestionDisplayText,
            hintText: subquestion.subquestionHintText || null,
            inputType: subquestion.inputType,
            varName: subquestion.serviceUserVarName,
            answer: prefillText || buildDisplayAnswer(answer),
            hasAnswer: answer !== null && answer !== undefined && answer !== '',
            options: allOptions,
            rawAnswer: answer,
            otherOptions: subquestion.otherOptions,
          });
        });

        // A question with no subquestions at all (defensive) keeps its
        // heading; one whose every row is gated off is dropped entirely.
        const hasSubquestions = Object.keys(question.subquestions || {}).length > 0;
        if (hasSubquestions && questionRows.length === 0) return;

        subsectionRows.push({
          type: 'question',
          title: question.questionTitle,
          tooltip: question.questionTooltip,
          wasSkipped: question.wasSkipped,
          skipButtonText: question.skipButtonText,
        });
        subsectionRows.push(...questionRows);
      });

      const hasQuestions = Object.keys(subsection.questions || {}).length > 0;
      if (hasQuestions && subsectionRows.length === 0) return;

      formattedQuestions.push({
        type: 'subsection',
        title: subsection.formSubsectionTitle,
      });
      formattedQuestions.push(...subsectionRows);
    });

    let scaleResults = null;
    if (formInfo.scaleMetadata?.scaleResults) {
      scaleResults = formInfo.scaleMetadata.scaleResults;
    } else if (formData.scaleResults && formData.scaleResults.complete === true) {
      scaleResults = formData.scaleResults;
    }

    return {
      formTitle,
      formattedQuestions,
      sourceDetails,
      scaleResults,
    };
  }, [isOpen, formKey, patientData, informantData, formInfo, isInformantForm]);

  const {
    formTitle,
    formattedQuestions,
    sourceDetails,
    scaleResults,
  } = panelContent;

  // Get maximum score for known scales
  // Keep in sync with maxScore values returned by scoring functions in ScalesFunctions.js
  const getMaxScore = () => {
    const defaultMaxScores = {
      'PHQ-9': 27,
      'GAD-7': 21,
      'ESS': 24,
      'MDQ': 13,
      'AQ-10': 10,
      'AQ-50': 50,
      'CAT-Q': 175,
      'CORE-10': 40,
      'ASRS': 18,
      'CNST': 5,
      'SPAT': 4,
      'BAARS-IV Current': 72,
      'BAARS-IV Childhood': 72,
      'BAARS-IV Current - Informant': 72,
      'BAARS-IV Childhood - Informant': 72,
      'ASRS-O Informant': 72,
      // SNAP-IV (18 items scored 0-3 ⇒ total 0-54). Include both clinical/internal labels we use.
      'SNAP-IV Childhood': 54,
      'SNAP-IV Childhood - Informant': 54,
      'Y-BOCS': 40,
      'AUDIT-C': 12,
    };
    
    // Use medical abbreviation from scale metadata if available
    const medicalName = formInfo.scaleMetadata?.internalName;
    if (medicalName && defaultMaxScores[medicalName]) {
      return defaultMaxScores[medicalName];
    }
    
    // Fallback: try to match form title with known scales
    for (const [key, maxScore] of Object.entries(defaultMaxScores)) {
      if (formTitle && formTitle.includes(key)) {
        return maxScore;
      }
    }
    
    // Final fallback by formKey when titles are shared across instruments.
    // Important for SNAP-IV vs BAARS where both use "Their Symptoms in Childhood".
    if (formKey === 'snapIVChildhoodInformant') {
      return 54;
    }

    // Default fallback
    return 100;
  };
  
  // When formInfo is null (dialog closed, selectedForm cleared) we still render
  // the Dialog.Root shell so Radix Presence can animate the exit. But we skip
  // the inner content to avoid null-reference crashes on formInfo properties.
  if (!formInfo) {
    return (
      <Dialog.Root open={false} onOpenChange={(open) => { if (!open) onClose?.(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
          {/*
            Positioning: we use a full-viewport flex shell + inner card instead of
            top-1/2 left-1/2 + translate(-50%,-50%). Our shared .radix-dialog-content-centered-motion
            class animates transform in @keyframes scaleIn — that replaces Tailwind translate
            utilities on the same element for a frame or two, so the box briefly anchors
            with top/left 50% only (dialog appears toward the bottom) then “jumps” when the
            animation transform wins. Flex centering avoids any transform on the Dialog.Content
            node; inset-motion (opacity-only) keeps smooth open/close without fighting layout.
          */}
          <Dialog.Content className="radix-dialog-content-inset-motion fixed inset-0 z-50 flex items-center justify-center p-4 outline-none">
            <div className="w-[95vw] max-w-[700px] max-h-[85vh] md:max-h-[65vh] overflow-y-auto rounded-lg bg-white p-4 md:p-6 shadow-lg">
              <Dialog.Title className="sr-only">Form Details</Dialog.Title>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
        {/* onClick on the flex wrapper closes the dialog when the user clicks the
            grey overlay area. Without this, clicks land on Dialog.Content (which covers
            the full viewport via inset-0) and Radix treats them as "inside" the dialog. */}
        <Dialog.Content
          className="radix-dialog-content-inset-motion fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          {/* Card uses flex-col so the header stays fixed while the body scrolls */}
          <div className="w-[95vw] max-w-[700px] max-h-[85vh] md:max-h-[65vh] flex flex-col rounded-lg bg-white shadow-lg">

          {/* Fixed header — title, source, timestamp and close button stay
              visible regardless of scroll position */}
          <div className="p-4 md:p-6 pb-3 border-b border-gray-200 shrink-0">
            <div className="flex justify-between items-center">
              <Dialog.Title className="text-base font-semibold text-gray-900">
                {displayMode === 'condensed' ? (formInfo.scaleMetadata?.internalName || formTitle) : formTitle}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="p-1 rounded-full hover:bg-gray-100">
                  <XIcon className="h-5 w-5 text-gray-500" />
                </button>
              </Dialog.Close>
            </div>
            {/* Source details for informant forms */}
            {isInformantForm && (
              <div className="flex items-center mt-1">
                <span className="text-sm font-medium text-gray-700 mr-1">Source:</span>
                <span className="text-sm text-gray-800">
                  {sourceDetails}
                </span>
              </div>
            )}
            {/* Last updated timestamp */}
            {patientData?.formData?.[formKey]?.lastUpdated && !isInformantForm && (
              <div className="flex items-center mt-1 text-xs text-gray-500">
                <ClockIcon className="h-3 w-3 mr-1" />
                Last updated: {formatDateTime(patientData.formData[formKey].lastUpdated)}
              </div>
            )}
            {informantData?.[formInfo.informantId]?.formData?.[formKey]?.lastUpdated && isInformantForm && (
              <div className="flex items-center mt-1 text-xs text-gray-500">
                <ClockIcon className="h-3 w-3 mr-1" />
                Last updated: {formatDateTime(informantData[formInfo.informantId].formData[formKey].lastUpdated)}
              </div>
            )}
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto p-4 md:p-6 pt-3">
          
          
          <div className="space-y-2">
            {formattedQuestions.map((item, index) => {
              // Check if this is the start of a new question block (for horizontal line placement)
              const isNewQuestionBlock = item.type === 'question' && index > 0 && 
                formattedQuestions[index - 1].type !== 'subsection';
              
              if (item.type === 'subsection') {
                // Hide subsection titles in condensed mode
                if (displayMode === 'condensed') {
                  return null;
                }
                return (
                  <div key={index} className="mb-3 mt-4 first:mt-0">
                    <h3 className="text-sm font-semibold text-gray-800 border-b pb-1.5 mb-1.5">{item.title}</h3>
                  </div>
                );
              }
              
              if (item.type === 'question') {
                return (
                  <div key={index}>
                    {/* Add horizontal line before new question blocks */}
                    {isNewQuestionBlock && (
                      <div className="border-t border-gray-200 my-3"></div>
                    )}
                    <div className="mb-2 mt-3">                   
                      <h4 className={`${displayMode === 'condensed' ? 'text-sm' : 'text-xs'} font-semibold text-gray-900`}>{item.title}</h4>
                      {item.tooltip && <p className="text-xs text-gray-500 mt-0.5">{item.tooltip}</p>}
                      <div className="text-xs text-gray-500 flex justify-start items-center mt-1">                      
                        {item.wasSkipped && (
                          <>
                          <span>Patient clicked</span>
                          <span className="text-xs bg-violet-50 shadow-sm border-violet-200 border text-gray-800  font-semibold px-2 py-1 rounded-md ml-2 whitespace-nowrap">
                            {item.skipButtonText}
                          </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              
              if (item.type === 'subquestion') {
                return (
                  <div key={index} className={`border rounded-md p-2.5 mb-2 ${
                    item.hasAnswer 
                      ? 'bg-violet-50 shadow-sm border-violet-200' 
                      : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                  }`}>
                    {/* Hide question text in condensed mode, except for MDQ */}
                    {(displayMode !== 'condensed' || (formInfo.scaleMetadata?.internalName === 'MDQ' || formTitle.includes('MDQ'))) && (
                      <>
                        <div className="text-xs font-medium mb-1">{item.text}</div>
                        {item.hintText && (
                          <div className="text-xs text-gray-400 mb-1 whitespace-pre-line">{item.hintText}</div>
                        )}
                      </>
                    )}
                    
                    {/* Display the answer — when the raw answer contains structured
                        otherValue data (e.g. "Other: please provide details"), FormattedAnswer
                        renders the free text inline in italic curly quotes so clinicians can
                        see what the patient typed without needing a separate section. */}
                    <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${
                      item.hasAnswer 
                        ? 'bg-white border-gray-200' 
                        : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                    }`}>
                      <FormattedAnswer
                        rawAnswer={item.rawAnswer}
                        displayText={item.answer}
                        inputType={item.inputType}
                      />
                    </div>
                    
                    {/* Display options based on display mode */}
                    {item.options && item.options.length > 0 && (
                      <div className={displayMode === 'condensed' ? '' : 'mt-2'}>
                        {displayMode === 'condensed' ? (
                          // Condensed mode: Only show selected answers
                          <div>
                            {/* <div className="text-xs text-gray-500 mb-1">Selected answer:</div>
                            <div className="space-y-1">
                              {item.options.map((option, optIndex) => {
                                const isSelected = item.hasAnswer && (() => {
                                  // For simple string match
                                  if (typeof item.rawAnswer === 'string') {
                                    return item.rawAnswer === option;
                                  }
                                  
                                  // For array of strings
                                  if (Array.isArray(item.rawAnswer) && typeof item.rawAnswer[0] === 'string') {
                                    return item.rawAnswer.includes(option);
                                  }
                                  
                                  // For array of objects with value property
                                  if (Array.isArray(item.rawAnswer) && typeof item.rawAnswer[0] === 'object') {
                                    return item.rawAnswer.some(ans => ans.value === option);
                                  }
                                  
                                  // For single object with value property
                                  if (item.rawAnswer && typeof item.rawAnswer === 'object' && 'value' in item.rawAnswer) {
                                    return item.rawAnswer.value === option;
                                  }
                                  
                                  return false;
                                })();

                                // Only render selected options in condensed mode
                                if (!isSelected) return null;

                                return (
                                  <div
                                    key={optIndex}
                                    className="text-xs p-2 rounded bg-violet-100 border border-violet-300 text-violet-700 font-medium inline-block"
                                  >
                                    {option}
                                  </div>
                                );
                              })}
                            </div> */}
                          </div>
                        ) : (
                          // Full mode: Show all options with selected ones highlighted
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Available options:</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                              {item.options.map((option, optIndex) => {
                                const isSelected = item.hasAnswer && (() => {
                                  // For simple string match
                                  if (typeof item.rawAnswer === 'string') {
                                    return item.rawAnswer === option;
                                  }
                                  
                                  // For array of strings
                                  if (Array.isArray(item.rawAnswer) && typeof item.rawAnswer[0] === 'string') {
                                    return item.rawAnswer.includes(option);
                                  }
                                  
                                  // For array of objects with value property
                                  if (Array.isArray(item.rawAnswer) && typeof item.rawAnswer[0] === 'object') {
                                    return item.rawAnswer.some(ans => ans.value === option);
                                  }
                                  
                                  // For single object with value property
                                  if (item.rawAnswer && typeof item.rawAnswer === 'object' && 'value' in item.rawAnswer) {
                                    return item.rawAnswer.value === option;
                                  }
                                  
                                  return false;
                                })();

                                return (
                                  <div
                                    key={optIndex}
                                    className={`text-xs p-1 rounded ${
                                      isSelected
                                        ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                                        : 'bg-gray-50 border border-gray-200 text-gray-500'
                                    }`}
                                  >
                                    {option}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                );
              }
              
              return null;
            })}
          </div>

          {/* Scale Results Section - moved to bottom */}
          {scaleResults && (
            <div className="mt-3 pt-4 border-t bg-white border border-gray-200 rounded-lg p-4">              
              <div className="space-y-2">
                <div className="text-sm text-gray-900">
                  <span className="font-semibold">Total Score:</span> <span>{(scaleResults.score !== undefined ? scaleResults.score : (scaleResults.totalScore !== undefined ? scaleResults.totalScore : '--'))} out of {getMaxScore()}</span>
                </div>                
                {(formInfo.scaleMetadata?.interpretation || scaleResults.interpretation) && 
                 !(formInfo.scaleMetadata?.internalName?.includes('BAARS-IV') || formTitle?.includes('BAARS-IV')) && (
                  <div className="text-sm text-gray-900">
                    <span className="font-semibold">Interpretation:</span> <span>{formInfo.scaleMetadata?.interpretation || scaleResults.interpretation}</span>
                  </div>
                )}
                {(formInfo.scaleMetadata?.impairment || scaleResults.impairment) && (
                  <div className="text-sm text-gray-900">
                    <span className="font-semibold">Functional Impact:</span> <span>{formInfo.scaleMetadata?.impairment || scaleResults.impairment}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>{/* end scrollable body */}
          </div>{/* end card */}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default FormDetailsPanel;